/**
 * Direct Delivery Receipt — PRD.265.
 *
 * Receive supplier stock WITHOUT a Purchase Order. Mirrors the PO-based
 * receive flow but the operator adds item lines manually from the receivable
 * item catalog (medicine / product / supply with track_stock = true).
 *
 * Costing rule (PRD.265 §12–13): bonus quantity increases stock but never the
 * supplier invoice amount, so `line_amount = quantity_received × unit_price`
 * and `effective_unit_cost = line_amount / (quantity_received + bonus)`.
 *
 * Stock behavior:
 *   Save Draft  → status='draft', NO stock movements.
 *   Finalize    → status='received', one inbound stock_movements row per line
 *                 with quantity_in = quantity_received + bonus. This is the
 *                 ONLY place this page writes stock_movements.
 *
 * Finalize is guarded by the DO's current DB status so an offline re-submit or
 * a double-click cannot post the inbound movements twice (PRD.265 §9).
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  canFinalizeDeliveryOrder,
  computeDirectLineAmount,
  computeDoTotals,
  computeEffectiveUnitCost,
  qActiveInventorySuppliers,
  qReceivableInventoryItems,
  validateDirectReceiptLine,
  type InventorySupplierRow,
  type ReceivableItemRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { NavIcon, useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import { InventoryPageShell } from './_shared';

// ─── Local draft ───

interface DirectLineDraft {
  id: string;
  itemId: string | null;
  itemName: string;
  itemSku: string | null;
  itemUnit: string | null;
  itemType: string | null;
  batchTracked: boolean;
  quantityReceived: string;
  bonusQuantity: string;
  unitPrice: string;
  batchNo: string;
  expiryDate: string;
  remarks: string;
}

function emptyLine(): DirectLineDraft {
  return {
    id: crypto.randomUUID(),
    itemId: null,
    itemName: '',
    itemSku: null,
    itemUnit: null,
    itemType: null,
    batchTracked: false,
    quantityReceived: '',
    bonusQuantity: '',
    unitPrice: '',
    batchNo: '',
    expiryDate: '',
    remarks: '',
  };
}

function lineAmountOf(line: DirectLineDraft): number {
  return computeDirectLineAmount(Number(line.quantityReceived) || 0, Number(line.unitPrice) || 0);
}

function effectiveCostOf(line: DirectLineDraft): number {
  return computeEffectiveUnitCost(
    lineAmountOf(line),
    Number(line.quantityReceived) || 0,
    Number(line.bonusQuantity) || 0,
  );
}

// ─── Page ───

export function DirectDeliveryReceiptPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const { deliveryOrderId } = useParams<{ deliveryOrderId?: string }>();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';
  const { db } = usePowerSyncDb();
  const fmt = useFormatMoney();

  const { data: supplierOptions = [] } = usePowerSyncQuery<InventorySupplierRow>(
    qActiveInventorySuppliers(),
  );

  const today = new Date().toISOString().slice(0, 10);
  const [doNumber, setDoNumber] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState('');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [reference, setReference] = useState('');
  const [dateRaised, setDateRaised] = useState(today);
  const [deliveryDate, setDeliveryDate] = useState(today);
  const [receivedDate, setReceivedDate] = useState(today);
  const [amountsAre, setAmountsAre] = useState<'tax_exclusive' | 'tax_inclusive'>('tax_exclusive');
  const [taxRate, setTaxRate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DirectLineDraft[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<null | 'draft' | 'finalize'>(null);
  const [hydrated, setHydrated] = useState(false);

  // Continue an existing DRAFT direct receipt: hydrate header + lines once.
  useEffect(() => {
    if (!deliveryOrderId || !db || hydrated) return;
    let cancelled = false;
    (async () => {
      const header = await db.getOptional<Record<string, unknown>>(
        `SELECT * FROM delivery_orders WHERE id = ? LIMIT 1`,
        [deliveryOrderId],
      );
      if (cancelled || !header) return;
      const lineRows = await db.getAll<Record<string, unknown>>(
        `SELECT * FROM delivery_order_lines WHERE delivery_order_id = ? ORDER BY created_at ASC`,
        [deliveryOrderId],
      );
      if (cancelled) return;
      setDoNumber((header.do_number as string) ?? '');
      setSupplierId((header.supplier_id as string) ?? null);
      setSupplierName((header.supplier_name as string) ?? '');
      setSupplierInvoiceNumber((header.supplier_invoice_number as string) ?? '');
      setReference((header.reference as string) ?? '');
      setDateRaised((header.date_raised as string) ?? today);
      setDeliveryDate((header.delivery_date as string) ?? today);
      setReceivedDate((header.received_date as string) ?? today);
      const aa = header.amounts_are as string;
      if (aa === 'tax_exclusive' || aa === 'tax_inclusive') setAmountsAre(aa);
      setTaxRate(header.tax_rate == null ? '' : String(header.tax_rate));
      setNotes((header.notes as string) ?? '');
      if (lineRows.length > 0) {
        setLines(
          lineRows.map((r) => ({
            id: (r.id as string) ?? crypto.randomUUID(),
            itemId: (r.inventory_item_id as string) ?? null,
            itemName: (r.item_name_snapshot as string) ?? '',
            itemSku: (r.sku_snapshot as string) ?? null,
            itemUnit: (r.unit_snapshot as string) ?? null,
            itemType: null,
            batchTracked: !!(r.batch_number_snapshot || r.inventory_batch_id),
            quantityReceived: String(r.quantity_received ?? ''),
            bonusQuantity: String(r.bonus_quantity_received ?? ''),
            unitPrice: String(r.unit_price ?? ''),
            batchNo: (r.batch_number_snapshot as string) ?? '',
            expiryDate: (r.expiry_date_snapshot as string) ?? '',
            remarks: (r.remarks as string) ?? '',
          })),
        );
      }
      setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [deliveryOrderId, db, hydrated, today]);

  function updateLine(id: string, patch: Partial<DirectLineDraft>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }
  function removeLine(id: string) {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== id)));
  }

  const lineAmounts = useMemo(() => lines.map(lineAmountOf), [lines]);
  const totals = useMemo(
    () => computeDoTotals(lineAmounts, Number(taxRate) || 0, amountsAre),
    [lineAmounts, taxRate, amountsAre],
  );

  const handleCancel = () => navigate('/inventory/delivery-orders');

  async function commit(isFinalize: boolean) {
    setError(null);
    if (!db) return setError('Local database not ready. Please try again in a moment.');
    if (!orgId) return setError('Missing organization context. Please sign in again.');
    if (!supplierName.trim()) return setError('Enter a supplier name.');
    const activeLines = lines.filter((l) => l.itemId);
    if (activeLines.length === 0) return setError('Add at least one item line.');

    // Per-line validation. Finalize enforces batch/expiry + positive quantities;
    // draft is permissive so a work-in-progress receipt can be saved.
    if (isFinalize) {
      if (!receivedDate) return setError('Received Date is required to finalize.');
      for (const l of activeLines) {
        const msg = validateDirectReceiptLine({
          hasItem: !!l.itemId,
          quantityReceived: Number(l.quantityReceived) || 0,
          bonusQuantity: Number(l.bonusQuantity) || 0,
          unitPrice: Number(l.unitPrice) || 0,
          batchTracked: l.batchTracked,
          batchNo: l.batchNo,
          expiryDate: l.expiryDate || null,
        });
        if (msg) return setError(`${l.itemName || 'Item'}: ${msg}`);
      }
    }

    // Idempotency / re-entry guard for continuing an existing draft.
    if (deliveryOrderId) {
      const existing = await db.getOptional<{ status: string | null }>(
        `SELECT status FROM delivery_orders WHERE id = ? LIMIT 1`,
        [deliveryOrderId],
      );
      if (existing && existing.status !== 'draft') {
        return setError(
          'This delivery order can no longer be edited (already finalized or cancelled).',
        );
      }
    }

    if (isFinalize) {
      const guard = canFinalizeDeliveryOrder({ status: 'draft', lineCount: activeLines.length });
      if (!guard.ok) return setError(guard.error ?? 'Cannot finalize.');
    }

    setSaving(isFinalize ? 'finalize' : 'draft');
    try {
      const now = new Date().toISOString();
      const doId = deliveryOrderId ?? crypto.randomUUID();
      const number = doNumber.trim() || `DDR-${now.slice(0, 10)}-${doId.slice(0, 4)}`;
      const status = isFinalize ? 'received' : 'draft';
      const rate = Number(taxRate) || 0;

      const headerParams = [
        doId,
        orgId,
        number,
        'manual_direct_receipt',
        supplierId,
        supplierName.trim(),
        supplierInvoiceNumber.trim() || null,
        reference.trim() || null,
        dateRaised || null,
        deliveryDate || null,
        isFinalize ? receivedDate : null,
        amountsAre,
        totals.subtotal,
        rate > 0 ? rate : null,
        totals.taxAmount,
        totals.total,
        notes.trim() || null,
        status,
        isFinalize ? userName || userId || null : null,
      ];

      if (deliveryOrderId) {
        // Continue a draft: update header, then replace draft lines wholesale.
        // Draft lines carry no stock movements, so delete + re-insert is safe.
        await db.execute(
          `UPDATE delivery_orders SET
            do_number = ?, source_type = ?, supplier_id = ?, supplier_name = ?, supplier_invoice_number = ?,
            reference = ?, date_raised = ?, delivery_date = ?, received_date = ?,
            amounts_are = ?, subtotal = ?, tax_rate = ?, tax_amount = ?, invoice_total = ?,
            notes = ?, status = ?, received_by = ?, updated_at = ?
           WHERE id = ?`,
          [
            number,
            'manual_direct_receipt',
            supplierId,
            supplierName.trim(),
            supplierInvoiceNumber.trim() || null,
            reference.trim() || null,
            dateRaised || null,
            deliveryDate || null,
            isFinalize ? receivedDate : null,
            amountsAre,
            totals.subtotal,
            rate > 0 ? rate : null,
            totals.taxAmount,
            totals.total,
            notes.trim() || null,
            status,
            isFinalize ? userName || userId || null : null,
            now,
            doId,
          ],
        );
        await db.execute(`DELETE FROM delivery_order_lines WHERE delivery_order_id = ?`, [doId]);
      } else {
        await db.execute(
          `INSERT INTO delivery_orders (
            id, org_id, do_number, source_type,
            supplier_id, supplier_name, supplier_invoice_number, reference,
            date_raised, delivery_date, received_date,
            amounts_are, subtotal, tax_rate, tax_amount, invoice_total, notes,
            status, received_by, created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [...headerParams, now, now],
        );
      }

      for (const line of activeLines) {
        const qtyReceived = Number(line.quantityReceived) || 0;
        const bonus = Number(line.bonusQuantity) || 0;
        const unitPrice = Number(line.unitPrice) || 0;
        const amount = computeDirectLineAmount(qtyReceived, unitPrice);
        const effectiveCost = computeEffectiveUnitCost(amount, qtyReceived, bonus);
        const totalQtyIn = qtyReceived + bonus;

        // Resolve / create batch for batch-tracked lines at finalize time.
        let batchId: string | null = null;
        const batchNumberSnapshot: string | null = line.batchNo.trim() || null;
        const batchExpirySnapshot: string | null = line.expiryDate || null;
        if (isFinalize && line.batchTracked && batchNumberSnapshot) {
          const existingBatch = await db.getOptional<{ id: string }>(
            `SELECT id FROM inventory_batches
             WHERE org_id = ? AND inventory_item_id = ? AND batch_number = ? LIMIT 1`,
            [orgId, line.itemId, batchNumberSnapshot],
          );
          if (existingBatch?.id) {
            batchId = existingBatch.id;
          } else {
            batchId = crypto.randomUUID();
            await db.execute(
              `INSERT INTO inventory_batches (
                id, org_id, inventory_item_id, batch_number, expiry_date, received_date,
                status, remarks, created_at, updated_at
              ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
              [
                batchId,
                orgId,
                line.itemId,
                batchNumberSnapshot,
                batchExpirySnapshot,
                receivedDate,
                'active',
                null,
                now,
                now,
              ],
            );
          }
        }

        const lineId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO delivery_order_lines (
            id, org_id, delivery_order_id, purchase_order_line_id,
            inventory_item_id, inventory_batch_id,
            item_name_snapshot, sku_snapshot, unit_snapshot, purchasing_unit_ordered,
            quantity_ordered, quantity_received, bonus_quantity_received,
            unit_price, unit_price_without_bonus, rate, amount,
            batch_number_snapshot, expiry_date_snapshot,
            remarks, created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            lineId,
            orgId,
            doId,
            null,
            line.itemId,
            batchId,
            line.itemName,
            line.itemSku,
            line.itemUnit,
            line.itemUnit,
            0,
            qtyReceived,
            bonus,
            unitPrice,
            effectiveCost,
            unitPrice,
            amount,
            batchNumberSnapshot,
            batchExpirySnapshot,
            line.remarks.trim() || null,
            now,
            now,
          ],
        );

        // Inbound stock movement — ONLY on finalize. Direct receipt uses a
        // distinct movement_type so the Stock Card can label it "Direct
        // Delivery Receipt" (PRD.265 §14). Bonus is included in stock in.
        if (isFinalize && totalQtyIn > 0) {
          await db.execute(
            `INSERT INTO stock_movements (
              id, org_id, inventory_item_id, inventory_batch_id,
              source_type, source_id, source_line_id,
              movement_type, quantity_in, quantity_out, unit,
              movement_date, remarks, created_by, created_at, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              line.itemId,
              batchId,
              'delivery_order',
              doId,
              lineId,
              'direct_delivery_received',
              totalQtyIn,
              0,
              line.itemUnit,
              receivedDate,
              line.remarks.trim() || notes.trim() || null,
              userId || null,
              now,
              now,
            ],
          );
        }
      }

      navigate('/inventory/delivery-orders');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed. Please try again.';
      setError(msg);
      setSaving(null);
    }
  }

  return (
    <InventoryPageShell
      title={deliveryOrderId ? 'Continue Direct Delivery Receipt' : 'Direct Delivery Receipt'}
      subtitle="Receive supplier stock without a Purchase Order. Finalize creates inbound stock movements; bonus quantity adds stock but not invoice amount."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <>
          <Button variant="outline" size="md" onClick={handleCancel} disabled={saving !== null}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="md"
            onClick={() => commit(false)}
            disabled={saving !== null}
          >
            {saving === 'draft' ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={() => commit(true)}
            disabled={saving !== null}
          >
            {saving === 'finalize' ? 'Finalizing…' : 'Finalize'}
          </Button>
        </>
      }
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          background: 'var(--bg-app, #f8fafc)',
        }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {error && (
            <div
              role="alert"
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--sp-3) var(--sp-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}

          <Card title="Delivery / Supplier Receiving">
            <Grid2>
              <Field label="DO No.">
                <TextInput value={doNumber} onChange={setDoNumber} placeholder="Auto-generated" />
              </Field>
              <Field label="Supplier" required>
                <SupplierSelector
                  options={supplierOptions}
                  supplierId={supplierId}
                  supplierName={supplierName}
                  onSelect={(s) => {
                    setSupplierId(s.id);
                    setSupplierName(s.supplier_name);
                  }}
                  onTypeFreeText={(name) => {
                    setSupplierId(null);
                    setSupplierName(name);
                  }}
                />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Supplier Invoice No.">
                <TextInput
                  value={supplierInvoiceNumber}
                  onChange={setSupplierInvoiceNumber}
                  placeholder="From supplier invoice"
                />
              </Field>
              <Field label="Reference">
                <TextInput
                  value={reference}
                  onChange={setReference}
                  placeholder="Internal reference / DR no."
                />
              </Field>
            </Grid2>
            <Grid3>
              <Field label="Date Raised">
                <TextInput type="date" value={dateRaised} onChange={setDateRaised} />
              </Field>
              <Field label="Delivery Date">
                <TextInput type="date" value={deliveryDate} onChange={setDeliveryDate} />
              </Field>
              <Field label="Received Date">
                <TextInput type="date" value={receivedDate} onChange={setReceivedDate} />
              </Field>
            </Grid3>
            <Grid3>
              <Field label="Amounts Are">
                <select
                  value={amountsAre}
                  onChange={(e) =>
                    setAmountsAre(e.target.value as 'tax_exclusive' | 'tax_inclusive')
                  }
                  style={{ ...inputStyle, height: 40, cursor: 'pointer' }}
                >
                  <option value="tax_exclusive">Tax Exclusive</option>
                  <option value="tax_inclusive">Tax Inclusive</option>
                </select>
              </Field>
              <Field label="Tax Rate (%)">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  placeholder="0"
                  style={{ ...inputStyle, height: 40 }}
                />
              </Field>
              <Field label="Notes">
                <TextInput value={notes} onChange={setNotes} placeholder="Optional" />
              </Field>
            </Grid3>
          </Card>

          <Card
            title="Items Received"
            actions={
              <Button
                variant="outline"
                size="md"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                + Add Line
              </Button>
            }
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1120 }}>
                <thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>Unit</Th>
                    <Th align="right">Qty Recv</Th>
                    <Th align="right">Bonus</Th>
                    <Th align="right">Unit Price</Th>
                    <Th align="right">Eff. Cost</Th>
                    <Th>Batch No.</Th>
                    <Th>Expiry</Th>
                    <Th align="right">Amount</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <DirectLineRow
                      key={line.id}
                      line={line}
                      orgId={orgId}
                      onChange={(patch) => updateLine(line.id, patch)}
                      onRemove={() => removeLine(line.id)}
                      canRemove={lines.length > 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-1)',
                alignItems: 'flex-end',
                paddingTop: 'var(--sp-2)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <TotalLine label="Subtotal" value={fmt(totals.subtotal)} />
              <TotalLine
                label={`Tax${Number(taxRate) ? ` (${Number(taxRate)}%)` : ''}`}
                value={fmt(totals.taxAmount)}
              />
              <TotalLine label="Total" value={fmt(totals.total)} strong />
            </div>
          </Card>
        </div>
      </div>
    </InventoryPageShell>
  );
}

function TotalLine({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-6)', alignItems: 'baseline', minWidth: 260 }}>
      <span
        style={{
          flex: 1,
          textAlign: 'right',
          fontSize: strong ? 'var(--font-size-md)' : 'var(--font-size-sm)',
          fontWeight: strong ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
          color: strong ? 'var(--text)' : 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          minWidth: 110,
          textAlign: 'right',
          fontSize: strong ? 'var(--font-size-md)' : 'var(--font-size-sm)',
          fontWeight: strong ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
          color: 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Line row ───

function DirectLineRow({
  line,
  orgId,
  onChange,
  onRemove,
  canRemove,
}: {
  line: DirectLineDraft;
  orgId: string;
  onChange: (patch: Partial<DirectLineDraft>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const fmt = useFormatMoney();
  const tdStyle: CSSProperties = {
    padding: '6px 8px',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'top',
  };
  const amount = lineAmountOf(line);
  const effCost = effectiveCostOf(line);
  return (
    <tr>
      <td style={{ ...tdStyle, minWidth: 220 }}>
        <ReceivableItemPicker
          orgId={orgId}
          value={line.itemName}
          onSelect={(row) =>
            onChange({
              itemId: row.id,
              itemName: row.item_name ?? '',
              itemSku: row.sku,
              itemUnit: row.inventory_unit,
              itemType: row.item_type,
              batchTracked: row.batch_tracked === 1,
              unitPrice: line.unitPrice || (row.cost_price != null ? String(row.cost_price) : ''),
            })
          }
        />
        {line.itemSku && (
          <div
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}
          >
            SKU: {line.itemSku}
          </div>
        )}
      </td>
      <td style={tdStyle}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {line.itemUnit ?? '—'}
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.quantityReceived}
          onChange={(e) => onChange({ quantityReceived: e.target.value })}
          placeholder="0"
          style={{ ...inputStyle, textAlign: 'right', width: 80 }}
        />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.bonusQuantity}
          onChange={(e) => onChange({ bonusQuantity: e.target.value })}
          placeholder="0"
          style={{ ...inputStyle, textAlign: 'right', width: 70 }}
        />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.unitPrice}
          onChange={(e) => onChange({ unitPrice: e.target.value })}
          placeholder="0.00"
          style={{ ...inputStyle, textAlign: 'right', width: 90 }}
        />
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          fontVariantNumeric: 'tabular-nums',
        }}
        title="Effective unit cost = amount ÷ (qty received + bonus)"
      >
        {effCost > 0 ? fmt(effCost) : '—'}
      </td>
      <td style={{ ...tdStyle, minWidth: 120 }}>
        {line.itemId && !line.batchTracked ? (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            Not batch-tracked
          </span>
        ) : (
          <input
            type="text"
            value={line.batchNo}
            onChange={(e) => onChange({ batchNo: e.target.value })}
            placeholder={line.batchTracked ? 'Required' : 'Optional'}
            style={{
              ...inputStyle,
              borderColor: line.batchTracked && !line.batchNo.trim() ? '#fca5a5' : 'var(--border)',
            }}
          />
        )}
      </td>
      <td style={{ ...tdStyle, minWidth: 140 }}>
        {line.itemId && !line.batchTracked ? (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>—</span>
        ) : (
          <input
            type="date"
            value={line.expiryDate}
            onChange={(e) => onChange({ expiryDate: e.target.value })}
            style={inputStyle}
          />
        )}
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {fmt(amount)}
      </td>
      <td style={tdStyle}>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Remove line"
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-muted)',
            cursor: canRemove ? 'pointer' : 'not-allowed',
            opacity: canRemove ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <NavIcon name="trash" />
        </button>
      </td>
    </tr>
  );
}

// ─── Supplier selector (mirrors CreatePurchaseOrderPage / PRD.207) ───

function SupplierSelector({
  options,
  supplierId,
  supplierName,
  onSelect,
  onTypeFreeText,
}: {
  options: InventorySupplierRow[];
  supplierId: string | null;
  supplierName: string;
  onSelect: (s: InventorySupplierRow) => void;
  onTypeFreeText: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = supplierName.trim().toLowerCase();
    if (!q) return options.slice(0, 12);
    return options
      .filter(
        (o) =>
          o.supplier_name.toLowerCase().includes(q) ||
          (o.contact_person ?? '').toLowerCase().includes(q) ||
          (o.phone ?? '').toLowerCase().includes(q) ||
          (o.email ?? '').toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [options, supplierName]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          value={supplierName}
          onChange={(e) => {
            onTypeFreeText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search supplier or type free text…"
          style={{ ...inputStyle, height: 40, flex: 1 }}
        />
        {supplierId && (
          <span
            title="Linked to a supplier master record"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 6px',
              height: 22,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success-bg, #dcfce7)',
              color: 'var(--color-success-text, #166534)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Linked
          </span>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 30,
          }}
        >
          {filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              onClick={() => {
                onSelect(s);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                background: supplierId === s.id ? 'var(--bg-surface-2)' : 'transparent',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  'var(--bg-hover, rgba(0,0,0,0.04))';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  supplierId === s.id ? 'var(--bg-surface-2)' : 'transparent';
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {s.supplier_name}
              </span>
              {(s.contact_person || s.phone || s.email) && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {[s.contact_person, s.phone, s.email].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Receivable item picker ───

function ReceivableItemPicker({
  orgId,
  value,
  onSelect,
}: {
  orgId: string;
  value: string;
  onSelect: (row: ReceivableItemRow) => void;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const brandTokens = useBrandTokens();

  useEffect(() => setQuery(value), [value]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // The dropdown is portaled to <body> to escape the table's horizontal
  // overflow clipping, so its position is measured from the input's rect and
  // kept in sync on scroll / resize while open.
  useEffect(() => {
    if (!open) return;
    function reposition() {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom, width: r.width });
    }
    reposition();
    function handlePointer(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const { data: rows = [] } = usePowerSyncQuery<ReceivableItemRow>(
    qReceivableInventoryItems(orgId, debounced),
  );

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search medicine / product / supply…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />
      {open &&
        rows.length > 0 &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{
              ...(brandTokens as CSSProperties),
              position: 'fixed',
              left: rect.left,
              top: rect.top + 4,
              width: Math.max(rect.width, 260),
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
              maxHeight: 280,
              overflowY: 'auto',
              zIndex: 1000,
            }}
          >
            {rows.slice(0, 30).map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => {
                  onSelect(row);
                  setQuery(row.item_name ?? '');
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--bg-hover, rgba(0,0,0,0.04))';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  {row.item_name}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    marginTop: 1,
                  }}
                >
                  {[
                    row.item_type,
                    row.sku,
                    row.inventory_unit && `Unit: ${row.inventory_unit}`,
                    row.batch_tracked === 1 && 'Batch-tracked',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── Small primitives (mirror CreatePurchaseOrderPage) ───

function Card({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Grid3({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: CSSProperties = {
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-family)',
};

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, height: 40 }}
    />
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}
